import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, MessageSquare, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    toast.success('Message sent successfully! We\'ll respond within 24-48 hours.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="bg-black/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button variant="ghost" className="text-white/70 hover:text-white">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <div className="h-6 w-px bg-white/20"></div>
            <h1 className="text-xl font-brand font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
              Contact Us
            </h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Information */}
          <Card className="glass-premium">
            <CardContent className="p-8">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Get in Touch</h2>
                  <p className="text-white/70">We're here to help</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6">
                  <div className="flex items-start space-x-3">
                    <Mail className="h-5 w-5 text-blue-400 mt-1" />
                    <div>
                      <h3 className="font-semibold text-blue-300 mb-2">General Support</h3>
                      <p className="text-white/70 text-sm mb-2">For general questions and support</p>
                      <a href="mailto:support@usexxx.com" className="text-blue-400 hover:text-blue-300">
                        support@usexxx.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-pink-500/10 border border-pink-500/20 rounded-lg p-6">
                  <div className="flex items-start space-x-3">
                    <MessageSquare className="h-5 w-5 text-pink-400 mt-1" />
                    <div>
                      <h3 className="font-semibold text-pink-300 mb-2">DMCA & Legal</h3>
                      <p className="text-white/70 text-sm mb-2">Copyright claims and legal matters</p>
                      <a href="mailto:dmca@usexxx.com" className="text-pink-400 hover:text-pink-300">
                        dmca@usexxx.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-green-400 mt-1" />
                    <div>
                      <h3 className="font-semibold text-green-300 mb-2">Response Time</h3>
                      <p className="text-white/70 text-sm">
                        We typically respond to all inquiries within 24-48 hours during business days.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-white/10 pt-6">
                  <h3 className="font-semibold text-white mb-3">Frequently Asked Questions</h3>
                  <div className="space-y-3">
                    <details className="text-sm">
                      <summary className="text-white/80 cursor-pointer hover:text-white">How do I report inappropriate content?</summary>
                      <p className="text-white/60 mt-2 ml-4">
                        Please email us with the video URL and reason for reporting. We review all reports within 24 hours.
                      </p>
                    </details>
                    <details className="text-sm">
                      <summary className="text-white/80 cursor-pointer hover:text-white">How can I request content removal?</summary>
                      <p className="text-white/60 mt-2 ml-4">
                        If you own the copyright to content on our site, please submit a DMCA takedown notice.
                      </p>
                    </details>
                    <details className="text-sm">
                      <summary className="text-white/80 cursor-pointer hover:text-white">Do you accept content submissions?</summary>
                      <p className="text-white/60 mt-2 ml-4">
                        Currently, we only feature content from verified sources and partners.
                      </p>
                    </details>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Form */}
          <Card className="glass-premium">
            <CardContent className="p-8">
              <div className="flex items-center space-x-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Send className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Send Message</h2>
                  <p className="text-white/70">Fill out the form below</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/50"
                    placeholder="Your full name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-white">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/50"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-white">Subject</Label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-3 py-2 bg-white/5 border border-white/20 text-white rounded-md"
                    required
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="technical">Technical Support</option>
                    <option value="dmca">DMCA / Copyright</option>
                    <option value="content">Content Report</option>
                    <option value="business">Business Inquiry</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/50 min-h-[120px]"
                    placeholder="Please provide as much detail as possible..."
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white py-3"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="loading-spinner mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </div>
                  )}
                </Button>

                <p className="text-xs text-white/60 text-center">
                  By submitting this form, you agree to our Terms of Service and Privacy Policy.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;